import * as React from "react";
import {
  FormTabProps,
  FormGroupContextProvider,
  FormTabHeader,
  usePermissions,
  useResourceContext,
  useRecordContext,
} from "react-admin";
import { Stack } from "@mui/material";

import { canAccess } from "../canAccess";

/**
 * Replacement for the default <FormTab> that only renders a tab if the user has the right permissions.
 *
 * Add a name prop to the FormTab to define the sub-resource that the user needs to have the right permissions for.
 *
 * <FormTab> also only renders the child inputs for which the user has the 'write' permissions.
 *
 * @example
 * import { Edit, TabbedForm, TextInput } from 'react-admin';
 * import { FormTab } from '@react-admin/ra-rbac';
 *
 * const authProvider = {
 *     // ...
 *     getPermissions: () => Promise.resolve([
 *         { action: ['list', 'edit'], resource: 'products' },
 *         { action: 'write', resource: 'products.reference' },
 *         { action: 'write', resource: 'products.width' },
 *         { action: 'write', resource: 'products.height' },
 *         // 'products.description' is missing
 *         { action: 'write', resource: 'products.thumbnail' },
 *         // 'products.image' is missing
 *         { action: 'write', resource: 'products.tab.description' },
 *         { action: 'write', resource: 'products.tab.images' },
 *         // 'products.tab.stock' is missing
 *     ]),
 * };
 *
 * const ProductEdit = () => (
 *     <Edit>
 *         <TabbedForm>
 *             <FormTab label="Description" name="description">
 *                 <TextInput source="reference" />
 *                 <TextInput source="width" />
 *                 <TextInput source="height" />
 *                 // not displayed
 *                 <TextInput source="description" />
 *             </FormTab>
 *             <FormTab label="Images" name="images">
 *                 // not displayed
 *                 <TextInput source="image" />
 *                 <TextInput source="thumbnail" />
 *             </FormTab>
 *             // not displayed
 *             <FormTab label="Stock" name="stock">
 *                 <TextInput source="stock" />
 *             </FormTab>
 *         </TabbedForm>
 *     </Edit>
 * );
 */
export const FormTab = (props: FormTabWithPermissionsProps) => {
  const {
    className,
    contentClassName,
    children,
    hidden,
    icon,
    intent,
    label,
    name,
    onChange,
    path,
    resource,
    syncWithLocation,
    value,
    ...rest
  } = props;
  const resourceFromContext = useResourceContext();
  const recordFromContext = useRecordContext();
  const { permissions, isLoading } = usePermissions();

  const renderHeader = () => (
    <FormTabHeader
      label={label}
      value={value}
      icon={icon}
      className={className}
      syncWithLocation={syncWithLocation}
      onChange={onChange}
      sx={{
        display:
          (isLoading ||
            !canAccess({
              permissions,
              action: "write",
              resource: `${resourceFromContext}.tab.${name}`,
              record: recordFromContext,
            })) &&
          "none",
      }}
    />
  );

  const renderChildren = (children) => {
    return React.Children.map(children, (input: React.ReactElement) => {
      if (
        typeof (input.props as any).source === "undefined" &&
        (input.props as any).children
      ) {
        return (
          <input.type {...input.props}>
            {renderChildren((input.props as any).children)}
          </input.type>
        );
      } else {
        return input &&
          canAccess({
            permissions,
            action: "write",
            resource: `${resource}.${(input.props as any).source}`,
            record: recordFromContext,
          })
          ? input
          : null;
      }
    });
  };

  const renderContent = () => (
    <FormGroupContextProvider name={value.toString()}>
      <Stack
        alignItems="flex-start"
        sx={{ paddingLeft: 1, paddingRight: 1 }}
        style={hidden ? hiddenStyle : null}
        className={contentClassName}
        id={`tabpanel-${value}`}
        aria-labelledby={`tabheader-${value}`}
        // Set undefined instead of false because WAI-ARIA Authoring Practices 1.1
        // notes that aria-hidden="false" currently behaves inconsistently across browsers.
        aria-hidden={hidden || undefined}
        {...rest}
      >
        {canAccess({
          permissions,
          action: "write",
          resource: `${resourceFromContext}.tab.${name}`,
          record: recordFromContext,
        }) && renderChildren(children)}
      </Stack>
    </FormGroupContextProvider>
  );

  // if (
  //     isLoading ||
  //     !canAccess({
  //         permissions,
  //         action: 'write',
  //         resource: `${resourceFromContext}.tab.${name}`,
  //         record: recordFromContext,
  //     })
  // ) {
  //     return null;
  // }

  return intent === "header" ? renderHeader() : renderContent();
};

const hiddenStyle = { display: "none" };

FormTab.displayName = "FormTab";

export interface FormTabWithPermissionsProps extends FormTabProps {
  name: string;
}
